import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { formatTimestamp } from "../../../utils/utils";
import { formatMetric, gradeOf, interfaceLabel, metricQuality, qualityColor } from "../../../utils/netquality";

const M_PER_DEG_LAT = 110574;
const M_PER_DEG_LON = 111320;
// Sin centrado guardado ni mediciones: Perú entero (igual que la página Empresa).
const FALLBACK_VIEW = { lat: -9.19, lng: -75.02, zoom: 5 };

const deviceIcon = L.icon({
  iconUrl: "/map/markers/marker-icon.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: "/map/markers/marker-shadow.png",
  shadowSize: [41, 41],
});

function cellBounds(cell) {
  const half = cell.cell_m / 2;
  const dLat = half / M_PER_DEG_LAT;
  const dLon = half / (M_PER_DEG_LON * Math.cos((cell.lat * Math.PI) / 180));
  return [[cell.lat - dLat, cell.lon - dLon], [cell.lat + dLat, cell.lon + dLon]];
}

function escapeHtml(text) {
  return String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Mapa Leaflet con las celdas coloreadas por la métrica elegida, los
 * marcadores de los equipos y las zonas que cambiaron. Es un mapa de
 * "valor": cada celda muestra el promedio de lo medido en ella, no cuántas
 * veces se midió, así una zona muy recorrida no parece mejor que otra.
 */
export default function CoverageMap({ center, cells, metric, iface, devices, changes, showChanges, focus, onCellSelect }) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const cellLayerRef = useRef(null);
  const deviceLayerRef = useRef(null);
  const changeLayerRef = useRef(null);
  const fittedRef = useRef(false);
  const onCellSelectRef = useRef(onCellSelect);
  onCellSelectRef.current = onCellSelect;

  // Inicialización única.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    const map = L.map(containerRef.current, { preferCanvas: true });
    L.tileLayer(process.env.REACT_APP_MAP_URL, { tileSize: 512, zoomOffset: -1 }).addTo(map);
    cellLayerRef.current = L.layerGroup().addTo(map);
    changeLayerRef.current = L.layerGroup().addTo(map);
    deviceLayerRef.current = L.layerGroup().addTo(map);
    const start = center && center.latitude !== null && center.longitude !== null && center.zoom !== null
      ? { lat: center.latitude, lng: center.longitude, zoom: center.zoom }
      : FALLBACK_VIEW;
    map.setView([start.lat, start.lng], start.zoom);
    fittedRef.current = start !== FALLBACK_VIEW;
    mapRef.current = map;
    return () => {
      map.off();
      map.remove();
      mapRef.current = null;
    };
    // Solo al montar: el centro guardado no cambia mientras se mira el mapa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // El centro y zoom configurados en Empresa llegan con la respuesta, después
  // de crear el mapa: se aplican en cuanto están si todavía no se encuadró.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || fittedRef.current || !center) return;
    if (center.latitude === null || center.longitude === null || center.zoom === null
      || center.latitude === undefined || center.longitude === undefined || center.zoom === undefined) return;
    map.setView([center.latitude, center.longitude], center.zoom);
    fittedRef.current = true;
  }, [center]);

  // Celdas: se redibujan al cambiar los datos o la métrica.
  useEffect(() => {
    const map = mapRef.current;
    const layer = cellLayerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const renderer = L.canvas({ padding: 0.5 });
    (cells || []).forEach((cell) => {
      const value = cell[metric];
      const q = metricQuality(metric, value, iface);
      const rect = L.rectangle(cellBounds(cell), {
        renderer,
        stroke: true,
        color: "rgba(255,255,255,0.35)",
        weight: 0.5,
        fillColor: qualityColor(q),
        fillOpacity: q === null ? 0.15 : 0.6,
        interactive: true,
      });
      rect.bindTooltip(
        `${t(`netmap.metrics.${metric}`)}: <b>${escapeHtml(formatMetric(metric, value))}</b>`,
        { sticky: true, direction: "top", opacity: 0.95 },
      );
      rect.on("click", () => {
        if (onCellSelectRef.current) onCellSelectRef.current(cell);
        const rows = [
          ["score", `${cell.score ?? "—"}${cell.score !== null && cell.score !== undefined ? ` (${gradeOf(cell.score)})` : ""}`],
          ["signal_dbm", formatMetric("signal_dbm", cell.signal_dbm)],
          ["rtt_avg_ms", formatMetric("rtt_avg_ms", cell.rtt_avg_ms)],
          ["jitter_ms", formatMetric("jitter_ms", cell.jitter_ms)],
          ["packet_loss_pct", formatMetric("packet_loss_pct", cell.packet_loss_pct)],
          ["down_mbps", formatMetric("down_mbps", cell.down_mbps)],
          ["up_mbps", formatMetric("up_mbps", cell.up_mbps)],
          ["gw_rtt_ms", formatMetric("gw_rtt_ms", cell.gw_rtt_ms)],
        ];
        if (iface === "lte") {
          rows.push(["rsrp", formatMetric("rsrp", cell.rsrp)], ["sinr", formatMetric("sinr", cell.sinr)]);
        }
        const html = `
          <h6>${escapeHtml(t("netmap.cellPopup.title", { m: cell.cell_m }))} · ${escapeHtml(interfaceLabel(iface))}</h6>
          <div style="color:#6B7280;margin-bottom:6px;">
            ${cell.n} ${escapeHtml(t("netmap.cellPopup.samples"))} · ${cell.devices} ${escapeHtml(t("netmap.cellPopup.devices"))}
            ${cell.connected_pct !== null && cell.connected_pct !== undefined ? ` · ${cell.connected_pct}% ${escapeHtml(t("netmap.cellPopup.connected"))}` : ""}
          </div>
          <table style="border-collapse:collapse;">
            ${rows
              .map(([key, text]) => `<tr><td style="color:#6B7280;padding:1px 8px 1px 0;">${escapeHtml(t(`netmap.metrics.${key}`))}</td><td style="font-weight:600;">${escapeHtml(text)}</td></tr>`)
              .join("")}
          </table>
          <div style="color:#6B7280;margin-top:6px;">${escapeHtml(t("netmap.cellPopup.last"))}: ${escapeHtml(formatTimestamp(cell.last_seen))}</div>
        `;
        L.popup({ maxWidth: 320 }).setLatLng([cell.lat, cell.lon]).setContent(html).openOn(map);
      });
      layer.addLayer(rect);
    });
    // Sin centrado guardado, la primera carga con datos ajusta la vista.
    if (!fittedRef.current && cells && cells.length > 0) {
      const bounds = L.latLngBounds(cells.map((c) => [c.lat, c.lon]));
      map.fitBounds(bounds.pad(0.2), { maxZoom: 16 });
      fittedRef.current = true;
    }
  }, [cells, metric, iface, t]);

  // Marcadores de equipos (última posición conocida).
  useEffect(() => {
    const layer = deviceLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    (devices || []).forEach((device) => {
      if (!device.position) return;
      const { latitude, longitude, created_at } = device.position;
      const marker = L.marker([latitude, longitude], { icon: deviceIcon });
      marker.bindTooltip(device.pdui_name, { permanent: true, direction: "top", offset: [0, -40], className: "netmap-device-tooltip" });
      const latest = device.latest || {};
      const scores = ["wifi", "lte", "ethernet"]
        .filter((k) => latest[k])
        .map((k) => `<tr><td style="color:#6B7280;padding-right:8px;">${interfaceLabel(k)}</td><td style="font-weight:600;color:${qualityColor(latest[k].score)}">${latest[k].score ?? "—"}</td></tr>`)
        .join("");
      marker.bindPopup(`
        <h6>${escapeHtml(device.pdui_name)}</h6>
        <div style="color:#6B7280;">${escapeHtml(t("netmap.marker.last"))}: ${escapeHtml(formatTimestamp(created_at))}</div>
        <div style="margin-top:4px;font-weight:600;">${escapeHtml(t("netmap.marker.lastScore"))}</div>
        <table>${scores}</table>
      `);
      layer.addLayer(marker);
    });
  }, [devices, t]);

  // Zonas que cambiaron.
  useEffect(() => {
    const layer = changeLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!showChanges || !changes) return;
    const items = [
      ...(changes.worsened || []).map((c) => ({ ...c, kind: "worsened" })),
      ...(changes.improved || []).map((c) => ({ ...c, kind: "improved" })),
    ];
    items.forEach((item) => {
      const icon = L.divIcon({
        className: `netmap-change-marker ${item.kind}`,
        html: "<span></span>",
        iconSize: [26, 26],
        iconAnchor: [13, 13],
      });
      const marker = L.marker([item.lat, item.lon], { icon, zIndexOffset: 500 });
      marker.bindTooltip(
        `${item.kind === "worsened" ? "▼" : "▲"} ${item.delta > 0 ? "+" : ""}${item.delta} · ${item.score_before} → ${item.score_after}`,
        { direction: "top", offset: [0, -12] },
      );
      layer.addLayer(marker);
    });
  }, [changes, showChanges]);

  // "Ver en el mapa" desde la lista de cambios.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focus) return;
    map.setView([focus.lat, focus.lon], Math.max(map.getZoom(), 16), { animate: true });
    const ring = L.circle([focus.lat, focus.lon], { radius: Math.max(focus.cell_m || 50, 30), color: "#ED8B00", weight: 3, fill: false });
    ring.addTo(map);
    const timer = setTimeout(() => map.removeLayer(ring), 4000);
    return () => {
      clearTimeout(timer);
      if (map.hasLayer(ring)) map.removeLayer(ring);
    };
  }, [focus]);

  return <div ref={containerRef} style={{ position: "absolute", inset: 0 }} />;
}
